import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Camera, Download, X } from 'lucide-react';
import { SERVICE_ICONS, DEFAULT_SERVICES } from '../../utils/constants';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { Logo } from '../common/Logo';
import { LoadingSpinner } from '../common/Badges';
import { Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ServiceGallery = () => {
  const { serviceType } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [serviceInfo, setServiceInfo] = useState(null);
  const pwa = usePWAInstall();

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/gallery?service_type=${serviceType}`).then(r => r.json()),
      fetch(`${API_URL}/api/site-content`).then(r => r.json())
    ]).then(([galleryData, contentData]) => {
      setImages(galleryData);
      const svc = contentData?.services?.[serviceType] || DEFAULT_SERVICES[serviceType];
      setServiceInfo(svc);
    }).catch(() => {
      setServiceInfo(DEFAULT_SERVICES[serviceType]);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  if (!serviceInfo && !loading) return <Navigate to="/" replace />;

  const Icon = SERVICE_ICONS[serviceType] || Zap;

  return (
    <div className="min-h-screen bg-black">
      <header className="bg-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="back-to-home-btn">
                <ArrowLeft className="w-5 h-5" />Back
              </button>
              <Logo />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/login')} className="btn-primary text-sm px-4 py-2" data-testid="gallery-login-btn">Customer Login</button>
              {!pwa.isInstalled && (
                <button onClick={pwa.install} className="flex items-center gap-2 text-sm px-4 py-2 bg-white text-black font-bold rounded hover:bg-gray-200 transition-all" data-testid="gallery-install-btn">
                  <Download className="w-4 h-4" />Install Free
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-red-500/20 flex items-center justify-center">
            <Icon className="w-8 h-8 text-red-500" />
          </div>
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white" data-testid="gallery-title">{serviceInfo?.name} Gallery</h1>
            <p className="text-gray-400 mt-1">{serviceInfo?.desc}</p>
          </div>
        </div>

        <div className="mb-10">
          <h2 className="text-lg font-semibold text-gray-300 mb-3">Services we provide:</h2>
          <div className="flex flex-wrap gap-2">
            {(serviceInfo?.jobs || []).map((job) => (
              <span key={job} className="bg-gray-800 text-gray-300 px-3 py-1.5 text-sm rounded">{job}</span>
            ))}
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : images.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
            {images.map((img) => (
              <motion.div key={img.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="group relative cursor-pointer overflow-hidden rounded-lg border border-gray-800 hover:border-red-500/50 transition-all"
                onClick={() => setSelectedImage(img)} data-testid={`gallery-image-${img.id}`}>
                <img src={img.image_url} alt={img.title} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <h3 className="text-white font-semibold">{img.title}</h3>
                  {img.description && <p className="text-gray-300 text-sm mt-1">{img.description}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="gallery-empty">
            <Camera className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No photos yet</h3>
            <p className="text-gray-500">Gallery photos for {serviceInfo?.name} coming soon!</p>
          </div>
        )}

        <div className="mt-12 text-center">
          <button onClick={() => navigate('/login')} className="btn-primary text-lg px-8 py-3" data-testid="gallery-request-service-btn">
            Request {serviceInfo?.name} Service
          </button>
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)} data-testid="gallery-lightbox">
          <button className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"><X className="w-8 h-8" /></button>
          <img src={selectedImage.image_url} alt={selectedImage.title} className="max-w-full max-h-[85vh] object-contain" />
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <h3 className="text-white text-xl font-semibold">{selectedImage.title}</h3>
            {selectedImage.description && <p className="text-gray-400 mt-1">{selectedImage.description}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceGallery;
